import { useEffect, useRef, useState } from "react";
import { TiArrowSortedDown } from "react-icons/ti";
import { useSelector } from "react-redux";
import Select from "react-select";
import { toast } from "react-toastify";

import PageHeader from "../common/PageHeader";

import {
  assignManagers,
  getCollaboratorsSuggestions,
  getOrganisationPeriodSetting,
  runAdminIsBalancesSynchronisation,
  runAdminIsClientEntitiesSynchronisation,
  runAdminIsClientRolesSynchronisation,
  runAdminIsClientsSynchronisation,
  runAdminIsCollaboratorSynchronisation,
  runAdminIsEncodageSynchronisation,
  runAdminIsInvoicePlanningSynchronisation,
  runAdminIsInvoiceSynchronisation,
  runAdminIsTaskSynchronisation,
  runAdminIsTvaBalancesSynchronisation,
  sendTimeSheetReminders,
  syncDebtors,
  syncInvoices,
} from "../../api";

import classNames from "classnames";
import { useQuery } from "react-query";
import _ from "../../i18n";
import { renderTeamYears } from "../../utils";
import { IconSend } from "../common/Icons";
import AdminISLogo from "./assets/admin-is-logo.jpg";
import styles from "./Synchronisation.module.scss";

const integrations = [
  {
    id: "#admin-is",
    name: "Syneton",
    src: AdminISLogo,
  },
  {
    id: "#recovr",
    name: "Recovr",
    src: "https://static.intercomassets.com/avatars/6472131/square_128/Group_39910-1744282782.png",
  },
];
const frenchMonths = {
  "01": "Janvier",
  "02": "Février",
  "03": "Mars",
  "04": "Avril",
  "05": "Mai",
  "06": "Juin",
  "07": "Juillet",
  "08": "Août",
  "09": "Septembre",
  10: "Octobre",
  11: "Novembre",
  12: "Décembre",
};

const Synchronisation = () => {
  const ref = useRef();
  const auth = useSelector((state) => state.auth);
  const [selected, setSelected] = useState("#admin-is");
  const [yearDivIsVisible, setYearDivIsVisible] = useState(false);
  const [filter, setFilter] = useState({
    year: new Date().getFullYear(),
    month: null,
    collaborator: null,
  });
  const [collaboratorOptions, setCollaboratorOptions] = useState([]);
  const [typeId, setTypeId] = useState(1);
  const [activeSyncType, setActiveSyncType] = useState(null);
  const [synchronisations, setSynchronisations] = useState({
    taskSync: false,
    invoiceSync: false,
    fixAmountSync: false,
    tvaSync: false,
    ipmSync: false,
    rolesSync: false,
    entitiesSync: false,
    recovrIDSync: false,
    recovrInvoiceSync: false,
    clientSync: false,
    encodageSync: false,
    recovrAssignManager: false,
    collaboratorSync: false,
  });
  const [syncStatus, setSyncStatus] = useState([]);
  const synchronisationOptions = [
    { id: 1, label: "TIME_SHEET", integration: "#admin-is" },
    { id: 2, label: "FACTURE", integration: "#admin-is" },
    { id: 3, label: "BALANCE ISOC", integration: "#admin-is" },
    { id: 4, label: "BALANCE IPM", integration: "#admin-is" },
    { id: 5, label: "RIC DECLARATION TVA", integration: "#admin-is" },
    { id: 10, label: "ENCODAGE", integration: "#admin-is" },
    { id: 6, label: "ROLES DES CLIENTS", integration: "#admin-is" },
    { id: 7, label: "RELATION ENTITÉ CLIENT", integration: "#admin-is" },
    { id: 14, label: "COLLABORATEURS", integration: "#admin-is" },
    { id: 8, label: "FACTURATION PLANNING", integration: "#admin-is" },
    { id: 9, label: "CLIENTS", integration: "#admin-is" },
    {
      id: 11,
      label: "Identification des dossiers",
      highlightText:
        "ID Recovr, lé référence externe et le portail de paiement",
      description: "Synchroniser l'identification des dossiers dans Recovr",
      integration: "#recovr",
    },
    {
      id: 12,
      label: "FACTURE",
      highlightText: "Paiements et Encours",
      description: "Synchroniser toutes les factures existantes dans Recovr",
      integration: "#recovr",
    },
    {
      id: 13,
      label: "Assignation des GDs",
      description: "Assigner les gestionnaires des comptes dans Recovr",
      integration: "#recovr",
    },
  ];

  const monthsOptions = Array.from({ length: 12 }, (_, i) => ({
    id: `m${i + 1}`,
    label: new Date(0, i)
      .toLocaleString("fr", { month: "long" })
      .replace(/^\w/, (c) => c.toUpperCase()),
  }));

  const handleMonthChange = (option) => {
    setFilter({ ...filter, month: option });
  };

  let collaboratorsdQuery = useQuery(
    ["getCollaborators", auth.token, auth.navCommunity.id],
    async () => {
      if (auth.token && auth.navCommunity) {
        try {
          const response = await getCollaboratorsSuggestions(
            auth.token,
            auth.navCommunity.id
          );
          let data = response.data.data;

          setCollaboratorOptions(
            data.map((c) => ({
              value: c.id,
              label: c.name,
            }))
          );
        } catch (e) {
          return null;
        }
      }
    }
  );

  // const fetchPeriodSettings = async () => {
  //   if (auth.token && auth.navCommunity) {
  //     try {
  //       const response = await getOrganisationPeriodSetting(
  //         auth.navCommunity.id,
  //         filter.year,
  //         auth.token
  //       );
  //       const data = response.data.data;
  //       const synchronisations = syncTypes.reduce((acc, type) => {
  //         acc[`${type}Sync`] = {
  //           status: data?.synchroDetails?.synchronization?.is_running?.[type],
  //           last_update:
  //             data?.synchroDetails?.synchronization?.last_update?.[type],
  //           progress: data?.synchroDetails?.synchronization?.progress?.[type],
  //         };
  //         return acc;
  //       }, {});
  //       setSynchronisations(synchronisations);
  //     } catch (e) {
  //       toast.error(_("error_loading_data"));
  //     }
  //   }
  // };

  const fetchPeriodSettings = async () => {
    if (auth.token && auth.navCommunity) {
      try {
        const response = await getOrganisationPeriodSetting(
          auth.navCommunity.id,
          new Date().getFullYear(),
          auth.token
        );
        const data = response.data.data;
        const syncDetails = data?.synchroDetails?.synchronization || {};
        const updatedSync = Object.keys(syncDetails.is_running || {}).reduce(
          (acc, type) => {
            acc[type] = {
              status: syncDetails.is_running[type],
              last_update: syncDetails.last_update?.[type],
              monthly_updates: syncDetails.monthly_updates?.[type],
              progress: syncDetails.progress?.[type] || 0,
            };
            return acc;
          },
          {}
        );
        setSyncStatus([
          { id: 1, label: "Tâches", isRunning: syncDetails.tasks },
          { id: 2, label: "Factures", isRunning: syncDetails.invoices },
          { id: 3, label: "ISOC", isRunning: syncDetails.ISOC },
          { id: 4, label: "IPM", isRunning: syncDetails.IPM },
          { id: 5, label: "TVA", isRunning: syncDetails.tva },
          { id: 6, label: "Rôles", isRunning: syncDetails.roles },
          { id: 7, label: "Entités", isRunning: syncDetails.entities },
          {
            id: 8,
            label: "Facture Planning",
            isRunning: syncDetails.invoiceLines,
          },
          { id: 9, label: "Client", isRunning: syncDetails.client_params },
          { id: 10, label: "ENCODAGE", isRunning: syncDetails.ENCODAGE },
          {
            id: 11,
            label: "Identification des dossiers",
            isRunning: syncDetails.recovr_ID,
          },
          {
            id: 12,
            label: "Facture Recovr",
            isRunning: syncDetails.recovr_invoice,
          },
          {
            id: 13,
            label: "Assigner GD Recovr",
            isRunning: syncDetails.recovr_manager,
          },
          {
            id: 14,
            label: "COLLABORATEURS",
            isRunning: syncDetails.collaborators,
          },
        ]);
        const activeType = Object.keys(updatedSync).find(
          (key) => updatedSync[key].status
        );
        setActiveSyncType(activeType || null);

        setSynchronisations(updatedSync);
      } catch (e) {
        toast.error(_("error_loading_data"));
      }
    }
  };

  useEffect(() => {
    fetchPeriodSettings();
    const intervalId = setInterval(fetchPeriodSettings, 20000);
    return () => clearInterval(intervalId);
  }, [auth.token, auth.navCommunity, filter.year]);

  const handleRunSynchronisation = async () => {
    const payload = {
      token: auth.token,
      organization: auth.navCommunity?.id,
      year: filter.year,
      type: typeId === 3 ? "ISOC" : typeId === 4 ? "IPM" : null,
      ...(filter.month?.id && { period: filter.month.id }),
      clientErpId: "ALL",
    };

    const syncTypes = {
      1: { key: "taskSync", action: runAdminIsTaskSynchronisation },
      2: { key: "invoiceSync", action: runAdminIsInvoiceSynchronisation },
      3: { key: "isocSync", action: runAdminIsBalancesSynchronisation },
      4: { key: "ipmSync", action: runAdminIsBalancesSynchronisation },
      5: { key: "tvaSync", action: runAdminIsTvaBalancesSynchronisation },
      6: { key: "rolesSync", action: runAdminIsClientRolesSynchronisation },
      7: {
        key: "entitiesSync",
        action: runAdminIsClientEntitiesSynchronisation,
      },
      8: {
        key: "fixAmountSync",
        action: runAdminIsInvoicePlanningSynchronisation,
      },
      9: {
        key: "clientSync",
        action: runAdminIsClientsSynchronisation,
      },
      10: {
        key: "ENCODAGE",
        action: runAdminIsEncodageSynchronisation,
      },
      14: {
        key: "collaboratorSync",
        action: runAdminIsCollaboratorSynchronisation,
      },
    };

    if (!syncTypes[typeId]) return;

    try {
      const { key, action } = syncTypes[typeId];
      setSynchronisations((prevState) => ({
        ...prevState,
        [key]: {
          ...prevState[key],
          isRunning: true,
        },
      }));
      setSynchronisations((prevState) => ({
        ...prevState,
        [key]: { ...prevState[key], status: true },
      }));
      setSyncStatus((prevState) =>
        prevState.map((item) =>
          item.id === typeId ? { ...item, isRunning: true } : item
        )
      );
      setActiveSyncType(key);
      await action(payload);

      toast.success(_("successfully_added"));
    } catch (e) {
      const errorMessage = e.response?.data?.errors?.[0]?.message || _("error");
      toast.error(errorMessage);
    }
  };

  const handleRunRecovrSynchronisation = async () => {
    const { token, navCommunity } = auth;

    const payload = {
      token,
      organizationId: navCommunity.id,
      collaboratorId: filter.collaborator,
    };

    const syncTypes = {
      11: { key: "recovrIDSync", action: syncDebtors },
      12: { key: "recovrInvoiceSync", action: syncInvoices },
      13: { key: "recovrAssignManager", action: assignManagers },
    };

    if (!syncTypes[typeId]) return;

    try {
      const { key, action } = syncTypes[typeId];
      setSynchronisations((prevState) => ({
        ...prevState,
        [key]: {
          ...prevState[key],
          isRunning: true,
        },
      }));
      setSynchronisations((prevState) => ({
        ...prevState,
        [key]: { ...prevState[key], status: true },
      }));
      setSyncStatus((prevState) =>
        prevState.map((item) =>
          item.id === typeId ? { ...item, isRunning: true } : item
        )
      );
      setActiveSyncType(key);
      await action(payload);

      toast.success(_("successfully_added"));
    } catch (e) {
      toast.error(_("error"));
      console.error("Recovr >> Err: ", e);
      return;
    }
  };

  const handleSendTimeSheetReminders = async () => {
    const { token, navCommunity } = auth;
    const payload = {
      token: token,
      organization: navCommunity?.id,
    };
    try {
      await sendTimeSheetReminders(payload);
      toast.success(_("successfully_sent"));
    } catch (e) {
      toast.error(_("error"));
      console.error("TimeSheet >> Reminders", e);
    }
  };

  const renderYearSelect = () =>
    selected == "#admin-is" ? (
      <div
        ref={ref}
        className={classNames("customed_select", {
          customed_select_opened: yearDivIsVisible,
        })}
        style={{ background: "#6d7f92", color: "#ffffff" }}
      >
        <div
          className="customed_select_header"
          style={{ height: "100%" }}
          onClick={() => setYearDivIsVisible(!yearDivIsVisible)}
        >
          <span>{filter.year}</span>
          <div className="actions">
            <TiArrowSortedDown />
          </div>
        </div>
        {yearDivIsVisible && (
          <div className="customed_select_body">
            {renderTeamYears().map((year, index) => (
              <div
                key={index}
                className="customed_select_body_option"
                onClick={() => setFilter({ ...filter, year })}
              >
                {year}
              </div>
            ))}
          </div>
        )}
      </div>
    ) : null;
  const formatDate = (dateString) => {
    if (!dateString) return "Date inconnue";
    const date = new Date(dateString);
    return (
      date.toLocaleDateString("fr-FR", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      }) +
      " à " +
      date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })
    );
  };
  const formatDateCustom = (dateStr) => {
    const date = new Date(dateStr);
    const day = date.getDate().toString().padStart(2, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${day} - ${month} - ${year} à ${hours}:${minutes}`;
  };
  useEffect(() => {
    // if (selected !== "#admin-is") return;
    const syncStatusLocal = [
      { id: 1, label: "Tâches", isRunning: synchronisations.tasks },
      { id: 2, label: "Factures", isRunning: synchronisations.invoices },
      { id: 3, label: "ISOC", isRunning: synchronisations.ISOC },
      { id: 4, label: "IPM", isRunning: synchronisations.IPM },
      { id: 5, label: "TVA", isRunning: synchronisations.tva },
      { id: 6, label: "Rôles", isRunning: synchronisations.roles },
      { id: 7, label: "Entités", isRunning: synchronisations.entities },
      {
        id: 8,
        label: "Facture Planning",
        isRunning: synchronisations.invoiceLines,
      },
      { id: 9, label: "clients", isRunning: synchronisations.client_params },
      { id: 10, label: "ENCODAGE", isRunning: synchronisations.ENCODAGE },
      {
        id: 11,
        label: "Identification des dossiers",
        isRunning: synchronisations.recovr_ID,
      },
      {
        id: 12,
        label: "Facture Recovr",
        isRunning: synchronisations?.recovr_invoice,
      },
      {
        id: 13,
        label: "Assign GD Recovr",
        isRunning: synchronisations?.recovr_manager,
      },
      {
        id: 14,
        label: "Collaborateurs",
        isRunning: synchronisations?.collaborators,
      },
    ];
    setSyncStatus(syncStatusLocal);
  }, [synchronisations, selected]);

  const renderSynchronisationSection = () => {
    const currentSync =
      syncStatus && syncStatus.find((sync) => sync.id === typeId);
    if (currentSync && currentSync.isRunning && currentSync.isRunning?.status) {
      return (
        <div className={styles.synchronisation_container}>
          <div
            className={classNames(
              styles.progress_bar,
              styles.gradiant_is_runing_synchro
            )}
          >
            {currentSync.isRunning?.progress === 0
              ? "Récupération des données"
              : currentSync.isRunning?.progress < 100
              ? currentSync.isRunning?.progress + "%"
              : "Cleaning data"}
          </div>
        </div>
      );
    }
    if (activeSyncType && currentSync) {
      return (
        <div className={styles.synchronisation_container}>
          <div
            className={classNames(
              styles.progress_bar,
              styles.gradiant_is_runing_synchro
            )}
          >
            {"Synchronisation des " + _(activeSyncType) + " est en cours..."}
          </div>
          <div className={styles.last_update}>
            Dernière mise à jour :{" "}
            {formatDate(currentSync.isRunning?.last_update)}
          </div>
          {activeSyncType && (
            <>
              {currentSync && (typeId === 1 || typeId === 2) && (
                <div>Mises à jour mensuelles :</div>
              )}
              <ul>
                {currentSync &&
                  (typeId === 1 || typeId === 2) &&
                  currentSync.isRunning?.monthly_updates &&
                  Object.entries(currentSync.isRunning.monthly_updates).map(
                    ([monthKey, date]) => {
                      const [month, year] = monthKey.split("-");
                      const monthName = frenchMonths[month] || monthKey;
                      return (
                        <li key={monthKey}>
                          {monthName} : {formatDateCustom(date)}
                        </li>
                      );
                    }
                  )}
              </ul>
            </>
          )}
        </div>
      );
    }

    return (
      <>
        <div className={styles.params}>
          {renderYearSelect()}
          {selected == "#admin-is" &&
            (typeId === 1 || typeId === 2 || typeId === 8) && (
              <div className={styles.months}>
                <span>Sélectionner le mois souhaité : </span>
                <Select
                  options={monthsOptions}
                  value={
                    monthsOptions.find(
                      (option) => option.id === filter.month?.id
                    ) || null
                  }
                  onChange={(option) => setFilter({ ...filter, month: option })}
                  getOptionLabel={(e) => `${e.label}`}
                  getOptionValue={(e) => e.id}
                  placeholder="Choisir un mois"
                  styles={{
                    control: (base) => ({
                      ...base,
                      width: "200px",
                      minWidth: "200px",
                    }),
                    menu: (base) => ({
                      ...base,
                      width: "200px",
                    }),
                  }}
                />
              </div>
            )}
          {selected == "#recovr" && typeId === 13 && (
            <div className={styles.months}>
              <span>Sélectionner GD: </span>
              <Select
                options={collaboratorOptions}
                alue={
                  collaboratorOptions.find(
                    (option) => option.value === filter.collaborator
                  ) || null
                }
                onChange={(option) =>
                  setFilter({ ...filter, collaborator: parseInt(option.value) })
                }
                getOptionLabel={(e) => `${e.label}`}
                getOptionValue={(e) => e.id}
                placeholder="Choisir un GD"
                styles={{
                  control: (base) => ({
                    ...base,
                    width: "200px",
                    minWidth: "200px",
                  }),
                  menu: (base) => ({
                    ...base,
                    width: "200px",
                  }),
                }}
              />
            </div>
          )}
        </div>
        <div className={styles.footer_actions}>
          <button
            className={styles.button_next}
            onClick={
              selected == "#admin-is"
                ? () => handleRunSynchronisation()
                : () => handleRunRecovrSynchronisation()
            }
          >
            Lancer
          </button>
        </div>
        <div style={{ color: "#666" }}>
          <div className={styles.last_update}>
            Dernière mise à jour :{" "}
            {currentSync && formatDate(currentSync.isRunning?.last_update)}
          </div>
          {currentSync &&
            (typeId === 1 || typeId === 2) &&
            currentSync.isRunning &&
            currentSync.isRunning.monthly_updates &&
            Object.entries(currentSync.isRunning.monthly_updates) && (
              <div style={{ position: "flex" }}>
                <div>Mises à jour mensuelles :</div>
                <ul style={{ listStyle: "none", padding: 0 }}>
                  {Object.entries(currentSync.isRunning.monthly_updates).map(
                    ([monthKey, date]) => {
                      const [month, year] = monthKey.split("-");
                      const monthName = frenchMonths[month] || monthKey;
                      return (
                        <li
                          key={monthKey}
                          style={{
                            display: "flex",
                            gap: "10px",
                          }}
                        >
                          <span style={{ minWidth: "100px" }}>
                            {monthName} :
                          </span>
                          <span>{formatDateCustom(date)}</span>
                        </li>
                      );
                    }
                  )}
                </ul>
              </div>
            )}
        </div>
      </>
    );
  };

  return (
    <div className="page-content">
      <PageHeader header={_("gestion des synchronisations")} />
      <div className={styles.page_content}>
        <div className={classNames(styles.content)}>
          <div className={styles.bloc}>
            <div className={styles.title}>Synchronisation</div>
            <div
              style={{
                display: "flex",
                gap: 15,
                alignItems: "center",
                padding: "10px 0",
              }}
            >
              {integrations.map((int) => (
                <div
                  key={int.id}
                  className={`${styles.content_card} ${
                    selected == int.id && styles.content_card_active
                  }`}
                  onClick={() => {
                    setTypeId(
                      synchronisationOptions.filter(
                        (op) => op.integration === int.id
                      )[0].id
                    );
                    setSelected(int.id);
                  }}
                >
                  <img src={int.src} alt="admin-is" height={50} />
                  <span>{int.name}</span>
                </div>
              ))}
            </div>
            <div className={classNames(styles.content, "grid-x")}>
              <div className={styles.client_types}>
                <div className={styles.sub_title}>Type de synchronisation</div>
                {synchronisationOptions
                  .filter((op) => op.integration === selected)
                  .map((item) => (
                    <div
                      key={item.id}
                      className={classNames(styles.types_labels, {
                        [styles.selected_type]: typeId === item.id,
                      })}
                      onClick={() => setTypeId(item.id)}
                    >
                      <span className={styles.type_label}>{item.label}</span>
                      <span className={styles.type_icon}>{">"}</span>
                    </div>
                  ))}
              </div>
              <div className={styles.synchronisation_section}>
                <div className={styles.synchronisation_header}>
                  <div className={styles.sub_title}>
                    <strong>
                      {synchronisationOptions.find((opt) => opt.id === typeId)
                        ?.label || ""}
                    </strong>
                    <span>
                      {synchronisationOptions.find((opt) => opt.id === typeId)
                        ?.highlightText || ""}
                    </span>
                  </div>
                  {selected == "#admin-is" && typeId === 1 && (
                    <button
                      className={styles.ts_reminder_btn}
                      onClick={() => handleSendTimeSheetReminders()}
                    >
                      <span>{_("sendTS")}</span>
                      <IconSend className={styles.ts_reminder_btn_icon} />
                    </button>
                  )}
                </div>
                <div className={styles.description}>
                  {selected == "#admin-is"
                    ? "Veuillez sélectionner les paramètres de synchronisation."
                    : synchronisationOptions.find((opt) => opt.id === typeId)
                        ?.description || ""}
                </div>
                {renderSynchronisationSection()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Synchronisation;
